import { useQuery } from "@apollo/client";
import { reviewerHasAccessToAllFacilities } from "encamp-compliance-engine/src/schemas/general/facilityAccess";
import { gql } from "generated-graphql";
import { Permission, UserType } from "generated-graphql/graphql";
import { useMemo } from "react";

export const REVIEWER_FACILITY_ACCESS_QUERY = gql(`
  query ReviewerFacilityAccess($id: ID!) {
    user(id: $id) {
      id
      type
      person { first last }
      facilities { id }
      UserTenant {
        tenantId
        role { permissions }
      }
    }
  }
`);

const STAFF_USER_TYPES: UserType[] = [UserType.StaffAdmin];

export function useReviewerFacilityAccessIssue({
  userId,
  name,
  submissionFacilityIds,
  tenantId,
  role = "Reviewer",
}: {
  userId: string | undefined;
  name: string;
  submissionFacilityIds: string[];
  /** Submission's tenant — scopes the WriteAllFacility exemption to match the backend rule. */
  tenantId?: string;
  /** Role label used in the warning text. Must stay in sync with renderIssueMessage's regex. */
  role?: "Reviewer" | "Signer";
}): string | null {
  const { data } = useQuery(REVIEWER_FACILITY_ACCESS_QUERY, {
    variables: { id: userId ?? "" },
    skip: !userId,
    fetchPolicy: "cache-first",
  });

  return useMemo(() => {
    const user = data?.user;
    if (!userId || !user) return null;

    const isStaff = STAFF_USER_TYPES.includes(user.type);

    // Scope the WriteAllFacility exemption to the submission's tenant, mirroring the
    // backend rule's `userHasPermissions(user, tenantId, …)`. A WriteAllFacility grant
    // in a different tenant must not exempt the user here. When the tenant is unknown,
    // fall back to checking every membership.
    const hasWriteAllFacility = (user.UserTenant ?? [])
      .filter((ut) => !tenantId || ut.tenantId === tenantId)
      .some((ut) => ut.role?.permissions?.includes(Permission.WriteAllFacility) ?? false);

    const hasAccess = reviewerHasAccessToAllFacilities({
      userFacilityIds: (user.facilities ?? []).map((f) => f.id),
      submissionFacilityIds,
      isStaff,
      hasWriteAllFacility,
    });

    if (hasAccess) return null;
    return `${role} ${name} must have access to all facilities to access this submission`;
  }, [data, userId, name, submissionFacilityIds, tenantId, role]);
}
